/**
 * /departments — health grid (21 cards).
 *
 * Layout: page header (title + summary count) + 3-column responsive
 * grid of DepartmentHealthCard. The grid is ordered by upstream-
 * provided `position` so the founder always sees the canonical layout.
 *
 * The sidebar's "Filter by health" chips drive the query-string
 * `?health=green|yellow|red`. The route accepts the filter and applies
 * it client-side (the upstream list is cheap — 21 rows).
 */
import type { DepartmentHealthState } from '@chaos/types/department';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';
import { z } from 'zod';

import { DepartmentHealthCard } from '../../../components/departments/department-health-card';
import { departmentsApi, departmentsKeys } from '../../../lib/api/departments';

const departmentsSearchSchema = z.object({
  health: z.enum(['green', 'yellow', 'red']).optional(),
  tab: z.enum(['escalations', 'my_queue']).optional(),
});

function DepartmentsIndex(): React.JSX.Element {
  const { health } = Route.useSearch();
  const listQ = useQuery({
    queryKey: departmentsKeys.list(),
    queryFn: () => departmentsApi.list(),
    refetchInterval: 15_000,
  });

  const departments = listQ.data?.departments ?? [];
  const filtered = React.useMemo(() => {
    const sorted = [...departments].sort((a, b) => {
      const ap = a.position ?? Number.MAX_SAFE_INTEGER;
      const bp = b.position ?? Number.MAX_SAFE_INTEGER;
      if (ap !== bp) return ap - bp;
      return a.displayName.localeCompare(b.displayName);
    });
    if (!health) return sorted;
    return sorted.filter((d) => d.health === (health as DepartmentHealthState));
  }, [departments, health]);

  const summary = React.useMemo(() => {
    if (!departments.length) return null;
    const counts = { healthy: 0, lagging: 0, failed: 0, unknown: 0 };
    for (const d of departments) {
      if (d.health === 'green') counts.healthy += 1;
      else if (d.health === 'yellow') counts.lagging += 1;
      else if (d.health === 'red') counts.failed += 1;
      else counts.unknown += 1;
    }
    return counts;
  }, [departments]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center justify-between gap-4 px-6 pt-3 pb-2">
        <h1 className="truncate text-[15px] font-semibold tracking-tight text-foreground">
          Departments
        </h1>
        {summary ? (
          <div className="flex items-center gap-3 text-[12px] text-muted-foreground">
            <SummaryChip dot="success" label="Healthy" value={summary.healthy} />
            <SummaryChip dot="warning" label="Lagging" value={summary.lagging} />
            <SummaryChip dot="error" label="Failed" value={summary.failed} />
            {summary.unknown > 0 ? (
              <SummaryChip dot="muted" label="Unknown" value={summary.unknown} />
            ) : null}
          </div>
        ) : null}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-2">
        {listQ.isLoading ? (
          <p className="text-[12px] text-muted-foreground/70">Loading 21 stewards…</p>
        ) : listQ.isError ? (
          <p className="text-[12px] text-destructive">
            Could not reach chaos-department-runtime. Try again in a moment.
          </p>
        ) : filtered.length === 0 ? (
          <p className="text-[12px] text-muted-foreground/70">
            {health
              ? `No departments are currently ${
                  health === 'green' ? 'healthy' : health === 'yellow' ? 'lagging' : 'failed'
                }.`
              : 'No departments registered.'}
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((dept) => (
              <DepartmentHealthCard key={dept.id} department={dept} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface SummaryChipProps {
  dot: 'success' | 'warning' | 'error' | 'muted';
  label: string;
  value: number;
}

function SummaryChip({ dot, label, value }: SummaryChipProps) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-hidden
        className={
          dot === 'success'
            ? 'h-1.5 w-1.5 rounded-full bg-success'
            : dot === 'warning'
              ? 'h-1.5 w-1.5 rounded-full bg-warning'
              : dot === 'error'
                ? 'h-1.5 w-1.5 rounded-full bg-destructive'
                : 'h-1.5 w-1.5 rounded-full bg-muted-foreground/40'
        }
      />
      <span>{label}</span>
      <span className="text-foreground tabular-nums">{value}</span>
    </span>
  );
}

export const Route = createFileRoute('/_authenticated/departments/')({
  component: DepartmentsIndex,
  validateSearch: (search) => departmentsSearchSchema.parse(search),
});
